import { ControlIcon } from './CompactControls';
import { loadAdminBanners } from '../utils/adminBanners';
import PageHeading from './PageHeading';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import LogoTray from './LogoTray';
import '../Search.css';
import './AdminPanel.css';
import './BannerStyles.css';
import PasswordChangeModal from './PasswordChangeModal';
import CreateUserModal from './CreateUserModal';
import ImageCropper from './ImageCropper';
import { USERNAME_HELP_TEXT, validateUsername } from '../utils/usernamePolicy';
import { getAnalyticsApiBaseUrls } from '../utils/analytics';
import { assetUrl } from '../utils/assetUrl';

const ANALYTICS_EXCLUDED_USERS_KEY = 'jaosve_analytics_excluded_users';
const DEFAULT_ANALYTICS_EXCLUDED_USERS = 'skaljic1';

const getInitialAnalyticsExcludedUsers = () => {
  try {
    return localStorage.getItem(ANALYTICS_EXCLUDED_USERS_KEY) || DEFAULT_ANALYTICS_EXCLUDED_USERS;
  } catch {
    return DEFAULT_ANALYTICS_EXCLUDED_USERS;
  }
};

const parseAnalyticsExcludedUsers = (value) => (
  (value || '')
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
);

const ANALYTICS_RECENT_COLUMN_FILTER_FIELDS = [
  'created_at',
  'event_name',
  'user',
  'target',
  'page'
];

const ANALYTICS_DETAIL_FILTER_FIELDS = [
  { field: 'ip_address', label: 'IP adresa', placeholder: '185.165.242.48' },
  { field: 'ip_location', label: 'IP lokacija', placeholder: 'Sarajevo, BA' },
  { field: 'browser', label: 'Browser', placeholder: 'Chrome 150.0.0.0' },
  { field: 'os', label: 'OS', placeholder: 'Windows' },
  { field: 'device_type', label: 'Tip uredjaja', placeholder: 'Desktop' },
  { field: 'screen', label: 'Ekran', placeholder: '1440 x 900' },
  { field: 'viewport', label: 'Viewport', placeholder: '1432 x 768' },
  { field: 'dpr', label: 'DPR', placeholder: '1' },
  { field: 'language', label: 'Jezik', placeholder: 'en-US' },
  { field: 'timezone', label: 'Timezone', placeholder: 'Europe/Sarajevo' },
  { field: 'platform', label: 'Platforma', placeholder: 'Windows' },
  { field: 'vendor', label: 'Vendor', placeholder: 'Google Inc.' },
  { field: 'cookies', label: 'Cookies', placeholder: 'Da / Ne' },
  { field: 'do_not_track', label: 'Do Not Track', placeholder: '1 / 0' },
  { field: 'touch_points', label: 'Touch points', placeholder: '0' },
  { field: 'cpu', label: 'CPU hint', placeholder: '3 cores' },
  { field: 'ram', label: 'RAM hint', placeholder: '8 GB' },
  { field: 'online', label: 'Online', placeholder: 'Da / Ne' },
  { field: 'connection', label: 'Konekcija', placeholder: '4g / 50 Mbps' },
  { field: 'referrer', label: 'Referrer', placeholder: 'google.com' },
  { field: 'user_agent', label: 'User agent', placeholder: 'Mozilla/5.0 ...', wide: true }
];

const ANALYTICS_RECENT_FILTER_FIELDS = [
  ...ANALYTICS_RECENT_COLUMN_FILTER_FIELDS,
  ...ANALYTICS_DETAIL_FILTER_FIELDS.map((filter) => filter.field)
];

const ANALYTICS_RECENT_FILTER_PARAMS = {
  created_at: 'recent_created_at',
  event_name: 'recent_event_name',
  user: 'recent_user',
  target: 'recent_target',
  page: 'recent_page',
  ip_address: 'detail_ip_address',
  ip_location: 'detail_ip_location',
  browser: 'detail_browser',
  os: 'detail_os',
  device_type: 'detail_device_type',
  screen: 'detail_screen',
  viewport: 'detail_viewport',
  dpr: 'detail_dpr',
  language: 'detail_language',
  timezone: 'detail_timezone',
  platform: 'detail_platform',
  vendor: 'detail_vendor',
  cookies: 'detail_cookies',
  do_not_track: 'detail_do_not_track',
  touch_points: 'detail_touch_points',
  cpu: 'detail_cpu',
  ram: 'detail_ram',
  online: 'detail_online',
  connection: 'detail_connection',
  referrer: 'detail_referrer',
  user_agent: 'detail_user_agent'
};

const getEmptyAnalyticsRecentFilters = () => ({
  created_at: '',
  event_name: '',
  user: '',
  target: '',
  page: ''
});

const normalizeAnalyticsRecentFilters = (filters) => (
  ANALYTICS_RECENT_FILTER_FIELDS.reduce((normalized, field) => {
    normalized[field] = (filters?.[field] || '').trim();
    return normalized;
  }, getEmptyAnalyticsRecentFilters())
);

const hasAnalyticsRecentFilters = (filters) => (
  ANALYTICS_RECENT_FILTER_FIELDS.some((field) => Boolean((filters?.[field] || '').trim()))
);

const appendAnalyticsRecentFilterParams = (params, filters) => {
  const normalizedFilters = normalizeAnalyticsRecentFilters(filters);
  ANALYTICS_RECENT_FILTER_FIELDS.forEach((field) => {
    if (normalizedFilters[field]) {
      params.set(ANALYTICS_RECENT_FILTER_PARAMS[field], normalizedFilters[field]);
    }
  });
};

function AdminPanel() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [categories, setCategories] = useState({});
  const [newCategory, setNewCategory] = useState('');
  const [newSubcategory, setNewSubcategory] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('categories');
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordChangeUserId, setPasswordChangeUserId] = useState(null);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [commercial, setCommercial] = useState({
    title: '',
    description: '',
    imageUrl: '',
    link: ''
  });
  const [commercialImage, setCommercialImage] = useState(null);
  const [originalImageFile, setOriginalImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [draggedSubcategory, setDraggedSubcategory] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [categoryMoveSearch, setCategoryMoveSearch] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedSubcategoryForMove, setSelectedSubcategoryForMove] = useState(null);
  const [selectedSubcategoryCurrentCategory, setSelectedSubcategoryCurrentCategory] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [commercialDimensions, setCommercialDimensions] = useState({
    width: 1200,
    height: 120,
    displayWidth: 1200,
    displayHeight: 120,
    cropX: 0,
    cropY: 0
  });
  const [hasUploadedOriginal, setHasUploadedOriginal] = useState(false);
  const uploadStateRef = useRef({ hasUploadedOriginal: false, hasCroppedImage: false });
  const [bannerFetching, setBannerFetching] = useState(false);
  const [banners, setBanners] = useState([]);
  const [selectedBannerId, setSelectedBannerId] = useState('');
  const [bannerDuration, setBannerDuration] = useState(5);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsTotal, setNotificationsTotal] = useState(0);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [analyticsError, setAnalyticsError] = useState('');
  const [analyticsExcludedUsers, setAnalyticsExcludedUsers] = useState(getInitialAnalyticsExcludedUsers);
  const [analyticsRecentFilters, setAnalyticsRecentFilters] = useState(getEmptyAnalyticsRecentFilters);
  const [analyticsRecentFilterInputs, setAnalyticsRecentFilterInputs] = useState(getEmptyAnalyticsRecentFilters);
  const [analyticsRecentLoading, setAnalyticsRecentLoading] = useState(false);
  const [analyticsRecentError, setAnalyticsRecentError] = useState('');
  const [analyticsClearing, setAnalyticsClearing] = useState(false);
  const [analyticsNotice, setAnalyticsNotice] = useState('');
  const analyticsRecentPanelRef = useRef(null);

  // Debug useEffect to track state changes
  useEffect(() => {
    console.log('📊 State changed:', {
      previewUrl: !!previewUrl,
      originalImageUrl: !!originalImageUrl,
      commercialImage: !!commercialImage,
      originalImageFile: !!originalImageFile,
      hasUploadedOriginal,
      showCropper,
      commercialDimensions
    });
  }, [previewUrl, originalImageUrl, commercialImage, originalImageFile, hasUploadedOriginal, showCropper, commercialDimensions]);
  const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    try {
      localStorage.setItem(ANALYTICS_EXCLUDED_USERS_KEY, analyticsExcludedUsers);
    } catch {
      // Local storage is optional; analytics filters can still work in memory.
    }
  }, [analyticsExcludedUsers]);

  useEffect(() => {
    // Check if user is admin
    const token = localStorage.getItem("token");
    if (!token) {
      navigate('/');
      return;
    }

    fetch(`${API_BASE_URL}/user/me`, {
      headers: { 
        "Authorization": `Bearer ${token}`,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
      .then(res => res.json())
      .then(data => {
        if (!data.admin) {
          navigate('/');
        } else {
          setIsAdmin(true);
          // Load current banner when admin panel loads
          fetchCurrentBanner();
          fetchUnreadCount();
          fetchSiteSettings();
        }
      })
      .catch(() => navigate('/'));
  }, [navigate]);

  const fetchSiteSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/site-settings`, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setMaintenanceMode(data.maintenance_mode);
      }
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const fetchAnalyticsSummary = async (
    days = analyticsDays,
    excludedUsersValue = analyticsExcludedUsers,
    recentFiltersValue = analyticsRecentFilters
  ) => {
    setAnalyticsLoading(true);
    setAnalyticsError('');
    setAnalyticsRecentError('');
    setAnalyticsNotice('');
    try {
      let lastStatus = null;
      let lastError = null;
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ days: String(days) });
      const excludedUsers = parseAnalyticsExcludedUsers(excludedUsersValue);
      if (excludedUsers.length > 0) {
        params.set('exclude_users', excludedUsers.join(','));
      }
      appendAnalyticsRecentFilterParams(params, recentFiltersValue);

      for (const apiBaseUrl of getAnalyticsApiBaseUrls()) {
        try {
          const response = await fetch(`${apiBaseUrl}/admin/analytics/summary?${params.toString()}`, {
            headers: {
              "Authorization": `Bearer ${token}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });

          lastStatus = response.status;

          if (response.ok) {
            const data = await response.json();
            setAnalyticsSummary(data);
            setAnalyticsError('');
            return;
          }

          if (![404, 502, 503, 504].includes(response.status)) {
            let message = 'Greska pri dohvatanju analitike';
            try {
              const errorData = await response.json();
              message = errorData.error || errorData.message || message;
            } catch {
              // Keep the default message.
            }
            setAnalyticsError(message);
            return;
          }
        } catch (error) {
          lastError = error;
        }
      }

      setAnalyticsSummary(null);
      setAnalyticsError(
        lastStatus === 404
          ? 'Analytics endpoint nije dostupan na API serveru koji koristi localhost. Pokrenite lokalni backend sa novim promjenama ili deployajte backend i migraciju.'
          : `Nije moguce dohvatiti analytics backend${lastError ? `: ${lastError.message}` : ''}.`
      );
    } catch (error) {
      console.error('Error fetching analytics summary:', error);
      setAnalyticsError('Greska pri dohvatanju analitike');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const keepAnalyticsRecentPanelInView = () => {
    window.requestAnimationFrame(() => {
      analyticsRecentPanelRef.current?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest'
      });
    });
  };

  const fetchAnalyticsRecentEvents = async (
    days = analyticsDays,
    excludedUsersValue = analyticsExcludedUsers,
    recentFiltersValue = analyticsRecentFilters
  ) => {
    setAnalyticsRecentLoading(true);
    setAnalyticsRecentError('');
    try {
      let lastStatus = null;
      let lastError = null;
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ days: String(days) });
      const excludedUsers = parseAnalyticsExcludedUsers(excludedUsersValue);
      if (excludedUsers.length > 0) {
        params.set('exclude_users', excludedUsers.join(','));
      }
      appendAnalyticsRecentFilterParams(params, recentFiltersValue);

      for (const apiBaseUrl of getAnalyticsApiBaseUrls()) {
        try {
          const response = await fetch(`${apiBaseUrl}/admin/analytics/recent?${params.toString()}`, {
            headers: {
              "Authorization": `Bearer ${token}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });

          lastStatus = response.status;

          if (response.ok) {
            const data = await response.json();
            setAnalyticsSummary((currentSummary) => (
              currentSummary
                ? {
                  ...currentSummary,
                  recent: data.recent || [],
                  recent_filters: data.recent_filters || {}
                }
                : currentSummary
            ));
            setAnalyticsRecentError('');
            return;
          }

          if (![404, 502, 503, 504].includes(response.status)) {
            let message = 'Greska pri filtriranju zadnjih eventa';
            try {
              const errorData = await response.json();
              message = errorData.error || errorData.message || message;
            } catch {
              // Keep the default message.
            }
            setAnalyticsRecentError(message);
            return;
          }
        } catch (error) {
          lastError = error;
        }
      }

      setAnalyticsRecentError(
        lastStatus === 404
          ? 'Recent analytics endpoint nije dostupan na API serveru.'
          : `Nije moguce filtrirati zadnje evente${lastError ? `: ${lastError.message}` : ''}.`
      );
    } catch (error) {
      console.error('Error fetching recent analytics events:', error);
      setAnalyticsRecentError('Greska pri filtriranju zadnjih eventa');
    } finally {
      setAnalyticsRecentLoading(false);
      keepAnalyticsRecentPanelInView();
    }
  };

  const handleClearAllAnalytics = async () => {
    if (analyticsClearing) return;

    const confirmed = window.confirm(
      'Obrisati sve analytics evente do sada? Ova akcija se ne moze ponistiti.'
    );
    if (!confirmed) return;

    setAnalyticsClearing(true);
    setAnalyticsError('');
    setAnalyticsRecentError('');
    setAnalyticsNotice('');

    try {
      let lastStatus = null;
      let lastError = null;
      const token = localStorage.getItem("token");

      for (const apiBaseUrl of getAnalyticsApiBaseUrls()) {
        try {
          const response = await fetch(`${apiBaseUrl}/admin/analytics/events`, {
            method: 'DELETE',
            headers: {
              "Authorization": `Bearer ${token}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });

          lastStatus = response.status;

          if (response.ok) {
            const data = await response.json();
            const emptyFilters = getEmptyAnalyticsRecentFilters();
            setAnalyticsRecentFilters(emptyFilters);
            setAnalyticsRecentFilterInputs(emptyFilters);
            await fetchAnalyticsSummary(analyticsDays, analyticsExcludedUsers, emptyFilters);

            const deletedCount = data.deleted || 0;
            const queuedCount = data.dropped_queued || 0;
            setAnalyticsNotice(
              queuedCount > 0
                ? `Obrisano ${deletedCount} eventa i ocisceno ${queuedCount} eventa iz reda.`
                : `Obrisano ${deletedCount} analytics eventa.`
            );
            return;
          }

          if (![404, 502, 503, 504].includes(response.status)) {
            let message = 'Greska pri brisanju analitike';
            try {
              const errorData = await response.json();
              message = errorData.error || errorData.message || message;
            } catch {
              // Keep the default message.
            }
            setAnalyticsError(message);
            return;
          }
        } catch (error) {
          lastError = error;
        }
      }

      setAnalyticsError(
        lastStatus === 404
          ? 'Clear all analytics endpoint nije dostupan na API serveru.'
          : `Nije moguce obrisati analitiku${lastError ? `: ${lastError.message}` : ''}.`
      );
    } catch (error) {
      console.error('Error clearing analytics:', error);
      setAnalyticsError('Greska pri brisanju analitike');
    } finally {
      setAnalyticsClearing(false);
    }
  };

  const handleToggleMaintenance = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/site-settings/maintenance`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({ enabled: !maintenanceMode })
      });
      if (response.ok) {
        setMaintenanceMode(!maintenanceMode);
      } else {
        setError('Greška pri ažuriranju maintenance moda');
      }
    } catch (error) {
      setError('Greška pri ažuriranju maintenance moda');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/categories`, {
        headers: { 
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Raw categories data from API:', data);
        
        // For now, just use the database data directly
        // This should show the changes from drag and drop immediately
        console.log('Setting categories to:', data);
        
        // Force React to detect the change by creating a new object
        setCategories(prevCategories => {
          const newCategories = { ...data };
          
          // Ensure OSTALO is always sorted alphabetically
          if (newCategories['Ostalo']) {
            newCategories['Ostalo'].sort();
          }
          
          console.log('Previous categories:', prevCategories);
          console.log('New categories:', newCategories);
          return newCategories;
        });
        
        // TODO: We can add the tag management back later if needed
        // but for now let's make sure drag and drop works
      }
    } catch (error) {
      setError('Greška pri dohvatanju kategorija');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: { 
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      setError('Greška pri dohvatanju korisnika');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ name: newCategory })
      });

      if (response.ok) {
        await fetchCategories();
        setNewCategory('');
      }
    } catch (error) {
      setError('Greška pri dodavanju kategorije');
    }
  };

  const handleAddSubcategory = async () => {
    if (!newSubcategory.trim() || !selectedCategory) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/subcategories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({
          category: selectedCategory,
          subcategory: newSubcategory
        })
      });

      if (response.ok) {
        await fetchCategories();
        setNewSubcategory('');
      }
    } catch (error) {
      setError('Greška pri dodavanju podkategorije');
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    if (categoryName === 'OSTALO') {
      setError('Ne možete izbrisati kategoriju "OSTALO"');
      return;
    }

    if (!window.confirm(`Jeste li sigurni da želite izbrisati kategoriju "${categoryName}"? Sve podkategorije će biti premještene u "OSTALO".`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/categories/${encodeURIComponent(categoryName)}`, {
        method: 'DELETE',
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (response.ok) {
        await fetchCategories();
      } else {
        const errorData = await response.json();
        setError(`Greška pri brisanju kategorije: ${errorData.error}`);
      }
    } catch (error) {
      setError('Greška pri brisanju kategorije');
    }
  };

  const handleEditCategory = (categoryName) => {
    setEditingCategory(categoryName);
    setEditingCategoryName(categoryName);
  };

  const handleCancelEditCategory = () => {
    setEditingCategory(null);
    setEditingCategoryName('');
  };

  const handleSaveCategory = async () => {
    if (!editingCategoryName.trim()) {
      setError('Ime kategorije ne može biti prazno');
      return;
    }

    if (editingCategoryName.trim() === editingCategory) {
      // No change, just cancel
      handleCancelEditCategory();
      return;
    }

    const oldCategoryName = editingCategory;
    const newCategoryName = editingCategoryName.trim();

    try {
      const response = await fetch(`${API_BASE_URL}/admin/categories/${encodeURIComponent(oldCategoryName)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ name: newCategoryName })
      });

      if (response.ok) {
        // Update selectedCategory if it matches the old category name
        if (selectedCategory === oldCategoryName) {
          setSelectedCategory(newCategoryName);
        }
        await fetchCategories();
        setEditingCategory(null);
        setEditingCategoryName('');
      } else {
        const errorData = await response.json();
        setError(`Greška pri ažuriranju kategorije: ${errorData.error}`);
      }
    } catch (error) {
      setError('Greška pri ažuriranju kategorije');
    }
  };

  const handleDeleteSubcategory = async (categoryName, subcategory) => {
    console.log('handleDeleteSubcategory called with:', { categoryName, subcategory });

    // If deleting from Ostalo, permanently delete. Otherwise, move to Ostalo.
    // Check for both 'Ostalo' and 'OSTALO' to handle any case variations
    if (categoryName === 'Ostalo' || categoryName === 'OSTALO') {
      // Permanent deletion from Ostalo
      if (!window.confirm(`Jeste li sigurni da želite trajno izbrisati podkategoriju "${subcategory}" iz baze podataka?`)) {
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/admin/subcategories/${encodeURIComponent(subcategory)}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            "Authorization": `Bearer ${localStorage.getItem("token")}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          body: JSON.stringify({
            category: categoryName
          })
        });

        if (response.ok) {
          await fetchCategories();
        } else {
          const errorData = await response.json();
          setError(`Greška pri brisanju podkategorije: ${errorData.error}`);
        }
      } catch (error) {
        setError('Greška pri brisanju podkategorije');
      }
    } else {
      // Move to Ostalo
      if (!window.confirm(`Jeste li sigurni da želite izbrisati podkategoriju "${subcategory}"? Podkategorija će biti premještena u "Ostalo".`)) {
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/admin/subcategories/move`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            "Authorization": `Bearer ${localStorage.getItem("token")}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          body: JSON.stringify({
            subcategory: subcategory,
            from_category: categoryName,
            to_category: 'Ostalo'
          })
        });

        if (response.ok) {
          await fetchCategories();
        } else {
          const errorData = await response.json();
          setError(`Greška pri premještanju podkategorije: ${errorData.error}`);
        }
      } catch (error) {
        setError('Greška pri premještanju podkategorije');
      }
    }
  };


  const handleDragStart = (result) => {
    console.log('Drag start:', result);
    setDraggedSubcategory(result.draggableId);
    setIsDragging(true);
  };

  const handleDragEnd = async (result) => {
    console.log('Drag end result:', result);
    setIsDragging(false);
    
    if (!result.destination) {
      console.log('No destination, cancelling drag');
      setDraggedSubcategory(null);
      return;
    }

    const { source, destination, draggableId } = result;
    const fromCategory = source.droppableId;
    const toCategory = destination.droppableId;

    console.log(`Moving ${draggableId} from ${fromCategory} to ${toCategory}`);

    // Don't allow dropping on the same category
    if (fromCategory === toCategory) {
      console.log('Same category, cancelling drag');
      setDraggedSubcategory(null);
      return;
    }

    try {
      console.log('Making API call to move subcategory');
      const response = await fetch(`${API_BASE_URL}/admin/subcategories/move`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({
          subcategory: draggableId,
          from_category: fromCategory,
          to_category: toCategory
        })
      });

      console.log('API response status:', response.status);
      
      if (response.ok) {
        console.log('Move successful, refreshing categories');
        await fetchCategories();
        console.log('Categories refreshed, current state:', categories);
      } else {
        const errorData = await response.json();
        console.error('API error:', errorData);
        setError(`Greška pri premještanju podkategorije: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Network error:', error);
      setError('Greška pri premještanju podkategorije');
    }

    setDraggedSubcategory(null);
  };

  const handleSubcategoryClick = (subcategory, currentCategory, event) => {
    // Prevent event propagation
    event.stopPropagation();
    
    // Open modal directly
    setCategoryMoveSearch('');
    setSelectedSubcategoryForMove(subcategory);
    setSelectedSubcategoryCurrentCategory(currentCategory);
    setShowCategoryModal(true);
  };

  const handleMoveSubcategoryToCategory = async (targetCategory) => {
    if (!selectedSubcategoryForMove || !selectedSubcategoryCurrentCategory) {
      return;
    }

    // Don't allow moving to the same category
    if (selectedSubcategoryCurrentCategory === targetCategory) {
      setShowCategoryModal(false);
      setSelectedSubcategoryForMove(null);
      setSelectedSubcategoryCurrentCategory(null);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/subcategories/move`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({
          subcategory: selectedSubcategoryForMove,
          from_category: selectedSubcategoryCurrentCategory,
          to_category: targetCategory
        })
      });

      if (response.ok) {
        await fetchCategories();
        setShowCategoryModal(false);
        setSelectedSubcategoryForMove(null);
        setSelectedSubcategoryCurrentCategory(null);
      } else {
        const errorData = await response.json();
        setError(`Greška pri premještanju podkategorije: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Network error:', error);
      setError('Greška pri premještanju podkategorije');
    }
  };

  // Test function to verify drag and drop is working
  const testDragAndDrop = () => {
    console.log('Testing drag and drop functionality');
    console.log('Categories state:', categories);
    console.log('react-beautiful-dnd imported:', !!DragDropContext);
  };

  const handleToggleUserAdmin = async (userId, isCurrentlyAdmin) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/admin`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ admin: !isCurrentlyAdmin })
      });

      if (response.ok) {
        await fetchUsers();
      }
    } catch (error) {
      setError('Greška pri ažuriranju admin statusa');
    }
  };

  const handleToggleUserVerification = async (userId, isCurrentlyVerified) => {
    try {
      console.log(`Toggling verification for user ${userId} to ${!isCurrentlyVerified}`);
      
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/verify`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ verified: !isCurrentlyVerified })
      });

      if (response.ok) {
        await fetchUsers();
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        setError(`Greška pri ažuriranju verifikacionog statusa: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Greška pri ažuriranju verifikacionog statusa');
    }
  };

  const handleToggleUserBan = async (userId, isCurrentlyBanned) => {
    try {
      console.log(`Toggling ban for user ${userId} to ${!isCurrentlyBanned}`);
      
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/ban`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ banned: !isCurrentlyBanned })
      });

      if (response.ok) {
        await fetchUsers();
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        setError(`Greška pri ažuriranju ban statusa: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Greška pri ažuriranju ban statusa');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Jeste li sigurni da želite izbrisati ovog korisnika?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
          method: 'DELETE',
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("token")}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (response.ok) {
          await fetchUsers();
        }
      } catch (error) {
        setError('Greška pri brisanju korisnika');
      }
    }
  };

  const handlePasswordChange = async (userId, newPassword) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ password: newPassword })
      });

      if (response.ok) {
        setPasswordChangeUserId(null); // Close modal
        // Show success message or notification here
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Greška pri promjeni lozinke');
      }
    } catch (error) {
      throw new Error(error.message || 'Greška pri promjeni lozinke');
    }
  };

  const handleCreateUser = async (userData) => {
    const usernamePolicyError = validateUsername(userData.username);
    if (usernamePolicyError) {
      setError(`Greška pri kreiranju korisnika: ${usernamePolicyError}`);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        await fetchUsers();
        setShowCreateUserModal(false);
      } else {
        const errorData = await response.json();
        setError(`Greška pri kreiranju korisnika: ${errorData.error}`);
      }
    } catch (error) {
      setError('Greška pri kreiranju korisnika');
    }
  };

  const handleUpdateCommercial = async () => {
    console.log('🔘 Update button clicked');
    console.log('🔘 Current state:', {
      commercialImage: !!commercialImage,
      previewUrl: !!previewUrl,
      originalImageUrl: !!originalImageUrl,
      commercialDimensions
    });
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError('Niste prijavljeni');
        return;
      }
      
            // Reset upload state for this session
      uploadStateRef.current = { hasUploadedOriginal: false, hasCroppedImage: false };
      
      // Save the original and cropped image together for the selected banner.
      const formData = new FormData();
      if (selectedBannerId) formData.append('bannerId', selectedBannerId);
      if (originalImageFile) {
        formData.append('originalImage', originalImageFile, originalImageFile.name);
        uploadStateRef.current.hasUploadedOriginal = true;
      }
      formData.append('durationSeconds', bannerDuration);
      
      // Always include dimensions if they exist
      if (commercialDimensions && (commercialDimensions.width > 0 || commercialDimensions.height > 0)) {
        formData.append('dimensions', JSON.stringify(commercialDimensions));
      }
      
      // Always include the banner link
      formData.append('link', commercial.link || '');

      // If we have a cropped image, save it as the main banner
      if (commercialImage && previewUrl && previewUrl !== originalImageUrl) {
        uploadStateRef.current.hasCroppedImage = true;
        formData.append('image', commercialImage, commercialImage.name);
        formData.append('isOriginal', 'false');
        console.log('📤 Uploading cropped image:', commercialImage.name);
      } else if (originalImageFile) {
        formData.append('image', originalImageFile, originalImageFile.name);
      }

      const response = await fetch(`${API_BASE_URL}/admin/commercial-banner`, {
        method: 'POST',
        headers: {
          "Authorization": `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: formData
      });

      if (response.ok) {
        const savedBanner = await response.json();
        setSelectedBannerId(String(savedBanner.id));
        await fetchCurrentBanner(savedBanner.id);
        setError('');
        
        // Check if we uploaded a new image in this session
        console.log('🔍 State check before hadNewImage calculation:');
        console.log('  - uploadStateRef.current:', uploadStateRef.current);
        console.log('  - hasUploadedOriginal:', hasUploadedOriginal);
        console.log('  - originalImageFile:', !!originalImageFile);
        console.log('  - commercialImage:', !!commercialImage);
        console.log('  - previewUrl:', !!previewUrl);
        console.log('  - originalImageUrl:', !!originalImageUrl);
        console.log('  - previewUrl !== originalImageUrl:', previewUrl !== originalImageUrl);
        
        const hadNewImage = uploadStateRef.current.hasUploadedOriginal || uploadStateRef.current.hasCroppedImage;
        
        console.log('Update commercial - analysis:', {
          hadNewImage,
          uploadStateRef: uploadStateRef.current,
          hasUploadedOriginal,
          originalImageFile: !!originalImageFile,
          commercialImage: !!commercialImage,
          previewUrl: !!previewUrl,
          originalImageUrl: !!originalImageUrl
        });
        
        if (hadNewImage) {
          console.log('Resetting state after image upload');
          // Reset state after successful image upload
          setCommercialImage(null);
          setOriginalImageFile(null);
          setPreviewUrl('');
          setOriginalImageUrl('');
          setHasUploadedOriginal(false);
          
          // Reset dimensions to default after image upload
          setCommercialDimensions({
            width: 1200,
            height: 120,
            displayWidth: 1200,
            displayHeight: 120,
            cropX: 0,
            cropY: 0
          });
        } else {
          console.log('Preserving state, refreshing banner data');
          // If only updating dimensions, preserve the current banner state
          // but refresh the banner data to get updated dimensions
          await fetchCurrentBanner(savedBanner.id);
        }
        
        // Trigger an event to notify other components about the banner update
        const event = new CustomEvent('commercialBannerUpdated', { 
          detail: { 
            timestamp: Date.now(),
            dimensions: commercialDimensions
          } 
        });
        window.dispatchEvent(event);
        
        alert('Reklama uspješno ažurirana!');
        
        // Force refresh the current banner data and reset state properly
        if (hadNewImage) {
          console.log('🔄 Resetting state and fetching fresh data after new image upload');
          // If we uploaded a new image, reset state and fetch fresh data
          setCommercialImage(null);
          setOriginalImageFile(null);
          setPreviewUrl('');
          setOriginalImageUrl('');
          setHasUploadedOriginal(false);
          setCommercialDimensions({
            width: 1200,
            height: 120,
            displayWidth: 1200,
            displayHeight: 120,
            cropX: 0,
            cropY: 0
          });
          
          // Fetch fresh banner data after a short delay
          setTimeout(() => {
            console.log('🔄 Fetching fresh banner data after state reset');
            fetchCurrentBanner(savedBanner.id);
          }, 500);
        } else {
          console.log('🔄 Refreshing banner data for dimension update only');
          // If only dimensions were updated, just refresh the banner data
          await fetchCurrentBanner(savedBanner.id);
        }
      } else {
        const errorData = await response.json();
        setError(`Greška pri ažuriranju reklame: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Greška pri ažuriranju reklame');
    }
  };

  const fetchCurrentBanner = async (requestedId) => {
    console.log('🔍 fetchCurrentBanner called');
    setBannerFetching(true);
    try {
      {
        const allBanners = await loadAdminBanners(API_BASE_URL);
        setBanners(allBanners.filter(banner => banner.id != null));
        const id = typeof requestedId === 'number' ? String(requestedId) : selectedBannerId;
        const data = allBanners.find(banner => String(banner.id) === id) || allBanners[0];
        if (!data) {
          setSelectedBannerId('');
          setPreviewUrl('');
          setOriginalImageUrl('');
          setCommercialImage(null);
          setBannerFetching(false);
          return;
        }
        setSelectedBannerId(data.id == null ? '' : String(data.id));
        setBannerDuration(data.durationSeconds || 5);
        console.log('📡 fetchCurrentBanner response:', data);
        setBannerFetching(false);
        
        console.log('🖼️ Setting previewUrl:', data.url);
        console.log('🖼️ Setting originalImageUrl:', data.originalUrl || data.url);
        
        // Set the banner URLs
        setPreviewUrl(assetUrl(data.url));
        setOriginalImageUrl(assetUrl(data.originalUrl || data.url));

        // Set the banner link
        if (data.link !== undefined) {
          setCommercial(prev => ({...prev, link: data.link || ''}));
        }
        
        // Create a dummy File object for the current banner so the UI recognizes it
        if (data.url) {
          const dummyFile = new File([''], 'current-banner.jpg', { type: 'image/jpeg' });
          setCommercialImage(dummyFile);
          console.log('🖼️ Setting commercialImage (dummy file)');
        } else {
          // Clear the commercial image if no URL
          setCommercialImage(null);
          console.log('🖼️ Clearing commercialImage (no URL)');
        }
        
        if (data.dimensions) {
          // Helper function to convert displayWidth to pixel value
          const convertDisplayWidth = (displayWidth, actualWidth) => {
            if (typeof displayWidth === 'string' && displayWidth.includes('%')) {
              // If it's a percentage, convert to pixel value based on actual width
              const percentage = parseFloat(displayWidth) / 100;
              return Math.round(actualWidth * percentage);
            }
            return displayWidth || actualWidth;
          };

          const newDimensions = {
            ...data.dimensions,
            displayWidth: convertDisplayWidth(data.dimensions.displayWidth, data.dimensions.width),
            displayHeight: data.dimensions.displayHeight || data.dimensions.height,
            cropX: data.dimensions.cropX || 0,
            cropY: data.dimensions.cropY || 0
          };
          console.log('📏 Setting commercialDimensions:', newDimensions);
          setCommercialDimensions(newDimensions);
        } else {
          console.log('⚠️ No dimensions in response');
        }
      }
    } catch (error) {
      console.error('❌ fetchCurrentBanner error:', error);
      setError('Greška pri dohvatanju trenutne reklame');
      setBannerFetching(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/notifications`, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count);
        setNotificationsTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/notifications/unread-count`, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleMarkNotificationRead = async (id) => {
    try {
      await fetch(`${API_BASE_URL}/admin/notifications/${id}/read`, {
        method: 'PUT',
        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API_BASE_URL}/admin/notifications/read-all`, {
        method: 'PUT',
        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all read:', error);
    }
  };

  const handleDeleteAllNotifications = async () => {
    if (!window.confirm('Jeste li sigurni da želite obrisati sve obavijesti?')) return;
    try {
      await fetch(`${API_BASE_URL}/admin/notifications/delete-all`, {
        method: 'DELETE',
        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error deleting all notifications:', error);
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await fetch(`${API_BASE_URL}/admin/notifications/${id}`, {
        method: 'DELETE',
        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const resizeImage = (file, maxWidth = 2000, maxHeight = 2000) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          let { width, height } = img;
          const originalWidth = width;
          const originalHeight = height;
          
          // Check if resizing is needed
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width *= ratio;
            height *= ratio;
            
            // Show warning about resizing
            setError(`Slika je prevelika (${originalWidth}×${originalHeight}px) i bit će smanjena na ${Math.round(width)}×${Math.round(height)}px za bolje performanse.`);
          }
          
          canvas.width = width;
          canvas.height = height;
          
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            const resizedFile = new File([blob], file.name, { type: file.type });
            resolve(resizedFile);
          }, file.type, 0.9);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jfif'];
      const fileType = file.type.toLowerCase();
      
      // Also check file extension for JFIF files since some browsers might not recognize the MIME type
      const extension = file.name.split('.').pop().toLowerCase();
      
      if (validTypes.includes(fileType) || extension === 'jfif' || extension === 'webp') {
        // Resize large images before processing
        resizeImage(file).then((resizedFile) => {
          console.log('📁 New image selected, resetting upload flag');
          // Reset upload flag for new image
          setHasUploadedOriginal(false);
          
          // Store the resized file as the original image file
          setOriginalImageFile(resizedFile);
          setCommercialImage(resizedFile);
          
          // Create preview URL for cropper
          const reader = new FileReader();
          reader.onloadend = () => {
            setCropperImage(reader.result);
            setOriginalImageUrl(reader.result); // Store original image URL
            setShowCropper(true);
          };
          reader.readAsDataURL(resizedFile);
        });
      } else {
        setError('Pogrešan format slike. Dozvoljeni formati su: JPG, PNG, GIF, WebP, JFIF');
      }
    }
  };



  const handleCropComplete = (croppedImage, dimensions) => {
    console.log('✂️ handleCropComplete called:', {
      hasCroppedImage: !!croppedImage,
      dimensions
    });
    
    // Update dimensions regardless of whether we have a cropped image
    // Use the actual display dimensions from the cropper
    const newDimensions = {
      ...dimensions,
      displayWidth: dimensions.displayWidth || dimensions.width, // Use actual pixel value
      displayHeight: dimensions.displayHeight || dimensions.height, // Use actual pixel value
      cropX: dimensions.cropX || 0,
      cropY: dimensions.cropY || 0
    };
    
    console.log('📏 Setting new dimensions:', newDimensions);
    setCommercialDimensions(newDimensions);
    setShowCropper(false);
    setCropperImage(null);
    
    if (croppedImage) {
      console.log('DEBUG: Cropping succeeded, got croppedImage:', croppedImage);
      // If we have a cropped image, use it as the main image
      setCommercialImage(croppedImage);
      
      // Create preview URL for the cropped image
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log('DEBUG: FileReader result for cropped image:', reader.result);
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(croppedImage);
      
      // Keep the original image URL for future re-cropping
      // The originalImageUrl should already be set from when the image was first loaded
      console.log('DEBUG: Original image URL preserved for re-cropping:', originalImageUrl);
    } else {
      // If no cropped image (CORS issue), just update the dimensions
      // Keep the existing image but update its display dimensions
      console.warn('DEBUG: CORS prevented image cropping, but dimensions updated:', dimensions);
      
      // Update the preview to show new dimensions
      if (previewUrl) {
        // Force re-render of preview with new dimensions
        // Check if this is a data URL - if so, don't append timestamp
        if (previewUrl.startsWith('data:')) {
          // For data URLs, just force a re-render by updating the state
          setPreviewUrl(prev => prev);
        } else {
          // For regular URLs, remove any existing timestamp parameters first
          const baseUrl = previewUrl.split('?')[0];
          setPreviewUrl(baseUrl + '?t=' + Date.now());
        }
      }
    }
    
    // DO NOT trigger the banner update event here - only when "Ažuriraj dimenzije" is pressed
    // This prevents immediate updates to Search.js
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setCropperImage(null);
    setCommercialImage(null);
    setOriginalImageFile(null);
    setOriginalImageUrl('');
  };

  useEffect(() => {
    console.log('🔄 activeSection changed to:', activeSection);
    if (activeSection === 'categories') {
      fetchCategories();
      testDragAndDrop(); // Test when categories section is opened
    } else if (activeSection === 'users') {
      fetchUsers();
    } else if (activeSection === 'commercial') {
      console.log('📂 Commercial section activated, fetching current banner');
      fetchCurrentBanner();
    } else if (activeSection === 'notifications') {
      fetchNotifications();
    } else if (activeSection === 'site-settings') {
      fetchSiteSettings();
    } else if (activeSection === 'analytics') {
      fetchAnalyticsSummary();
    }
  }, [activeSection]);

  useEffect(() => {
    if (users.length > 0) {
      const filtered = users.filter(user => 
        user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.name && user.name.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, users]);

  const handleUpdateUser = async (userId, updatedData) => {
    try {
      // Create a new object with only the fields we want to update
      const formattedData = {};

      // Only add fields that have values
      if (updatedData.username) {
        const usernamePolicyError = validateUsername(updatedData.username);
        if (usernamePolicyError) {
          setError(`Greška pri ažuriranju korisnika: ${usernamePolicyError}`);
          return;
        }
        formattedData.username = updatedData.username;
      }
      if (updatedData.email) formattedData.email = updatedData.email;
      if (updatedData.name) formattedData.name = updatedData.name;
      if (updatedData.birth_date) formattedData.birth_date = updatedData.birth_date;
      if (updatedData.phone) formattedData.phone = updatedData.phone;
      
      // Only include gender if it's one of the valid values
      if (['M', 'F', 'O'].includes(updatedData.gender)) {
        formattedData.gender = updatedData.gender;
      }

      // Always include these boolean fields
      formattedData.admin = !!updatedData.admin;
      formattedData.verified = !!updatedData.verified;

      console.log('Sending update with data:', formattedData);

      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify(formattedData)
      });

      if (response.ok) {
        await fetchUsers();
        setEditingUser(null);
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        setError(`Greška pri ažuriranju korisnika: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Greška pri ažuriranju korisnika');
    }
  };

  const changeBannerSelection = (id) => {
    setSelectedBannerId(id);
    setOriginalImageFile(null);
    setCommercialImage(null);
    setPreviewUrl('');
    setOriginalImageUrl('');
    if (id) fetchCurrentBanner(Number(id));
  };

  const startNewBanner = () => {
    setSelectedBannerId('');
    setCommercialImage(null);
    setOriginalImageFile(null);
    setPreviewUrl('');
    setOriginalImageUrl('');
    setCommercial(prev => ({...prev, link: ''}));
    setBannerDuration(5);
    setCommercialDimensions({width: 1200, height: 120, displayWidth: 1200, displayHeight: 120, cropX: 0, cropY: 0});
  };

  const moveBannerToPosition = (position) => {
    const ids = banners.map(banner => banner.id);
    const current = ids.indexOf(Number(selectedBannerId));
    if (current < 0 || current === position) return;
    ids.splice(position, 0, ids.splice(current, 1)[0]);
    updateBannerCollection('/admin/commercial-banners/order', 'PUT', {ids});
  };

  const updateBannerCollection = async (path, method, body, selectedId = selectedBannerId) => {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        ...(body ? {'Content-Type': 'application/json'} : {})
      },
      ...(body ? {body: JSON.stringify(body)} : {})
    });
    if (!response.ok) {
      const data = await response.json();
      setError(data.error || 'Greška pri ažuriranju reklama');
      return;
    }
    setError('');
    await fetchCurrentBanner(selectedId ? Number(selectedId) : undefined);
  };

  const formatAnalyticsDate = (value) => {
    if (!value) return '';
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;

      return date.toLocaleString('bs-BA', {
        timeZone: 'Europe/Sarajevo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return value;
    }
  };

  const getShortAnalyticsId = (value) => {
    if (!value) return '';
    const text = String(value);
    return text.length > 18 ? `${text.slice(0, 18)}...` : text;
  };

  const getAnalyticsIpLabel = (ipAddress, ipHash, countryLabel) => {
    if (ipAddress) return countryLabel ? `${ipAddress} (${countryLabel})` : ipAddress;
    if (ipHash) return `Hash ${getShortAnalyticsId(ipHash)}`;
    return '-';
  };

  const getAnalyticsProperty = (properties, keys) => {
    for (const key of keys) {
      const value = properties?.[key];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return undefined;
  };

  const formatAnalyticsBoolean = (value) => {
    if (value === true) return 'Da';
    if (value === false) return 'Ne';
    return '-';
  };

  const formatAnalyticsResolution = (width, height) => {
    if (width && height) return `${width} x ${height}`;
    if (width) return `${width} px sirine`;
    if (height) return `${height} px visine`;
    return '-';
  };

  const formatAnalyticsDetailValue = (value) => {
    if (value === undefined || value === null || value === '') return '-';
    if (typeof value === 'boolean') return formatAnalyticsBoolean(value);
    if (Array.isArray(value)) {
      const formattedItems = value
        .map((item) => {
          if (item && typeof item === 'object') {
            return [item.brand, item.version].filter(Boolean).join(' ');
          }
          return item;
        })
        .filter(Boolean);
      return formattedItems.length > 0 ? formattedItems.join(', ') : '-';
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const getAnalyticsBrowser = (event, properties) => {
    const brands = Array.isArray(properties.ua_brands) ? properties.ua_brands : [];
    const preferredBrand = brands.find((brand) => (
      brand?.brand &&
      !/not|chromium/i.test(brand.brand) &&
      /chrome|edge|firefox|safari|opera|samsung/i.test(brand.brand)
    ));
    if (preferredBrand) {
      return [preferredBrand.brand, preferredBrand.version].filter(Boolean).join(' ');
    }

    const userAgent = event.user_agent || '';
    const checks = [
      [/Edg\/([\d.]+)/, 'Microsoft Edge'],
      [/OPR\/([\d.]+)/, 'Opera'],
      [/SamsungBrowser\/([\d.]+)/, 'Samsung Browser'],
      [/Firefox\/([\d.]+)/, 'Firefox'],
      [/Chrome\/([\d.]+)/, 'Chrome'],
      [/Version\/([\d.]+).*Safari/, 'Safari'],
    ];

    for (const [pattern, label] of checks) {
      const match = userAgent.match(pattern);
      if (match) return `${label} ${match[1]}`;
    }
    return userAgent ? 'Nepoznat browser' : '-';
  };

  const getAnalyticsOs = (event, properties) => {
    if (properties.ua_platform) return properties.ua_platform;

    const userAgent = event.user_agent || '';
    const checks = [
      [/Android\s([\d.]+)/, 'Android'],
      [/(iPhone|iPad|iPod).*OS\s([\d_]+)/, 'iOS'],
      [/Windows NT/, 'Windows'],
      [/Mac OS X\s([\d_]+)/, 'macOS'],
      [/Linux/, 'Linux'],
    ];

    for (const [pattern, label] of checks) {
      const match = userAgent.match(pattern);
      if (!match) continue;
      const version = match[2] || match[1];
      return version && !/Windows|Linux/.test(label)
        ? `${label} ${String(version).replace(/_/g, '.')}`
        : label;
    }
    return userAgent ? 'Nepoznat OS' : '-';
  };

  const getAnalyticsDeviceType = (event, properties) => {
    const userAgent = event.user_agent || '';
    if (/iPad|Tablet/i.test(userAgent)) return 'Tablet';
    if (properties.ua_mobile === true || properties.is_mobile === true || /Android|iPhone|iPod|Mobile/i.test(userAgent)) {
      return 'Mobilni';
    }
    if (properties.ua_mobile === false || properties.is_mobile === false || userAgent) return 'Desktop';
    return '-';
  };

  const formatAnalyticsLocation = (properties) => {
    const location = properties.ip_location && typeof properties.ip_location === 'object'
      ? properties.ip_location
      : {};
    const locationText = [location.city, location.region, location.country].filter(Boolean).join(', ');
    if (locationText && location.timezone) return `${locationText} (${location.timezone})`;
    return locationText || '-';
  };

  const formatAnalyticsConnection = (properties) => {
    const parts = [];
    if (properties.connection_type) parts.push(properties.connection_type);
    if (properties.connection_downlink) parts.push(`${properties.connection_downlink} Mbps`);
    if (properties.connection_rtt) parts.push(`${properties.connection_rtt} ms`);
    if (properties.connection_save_data === true) parts.push('Data saver');
    return parts.length > 0 ? parts.join(' / ') : '-';
  };

  const getAnalyticsDetailRows = (event) => {
    const properties = event.properties || {};
    const language = [
      properties.language,
      Array.isArray(properties.languages) && properties.languages.length > 0
        ? properties.languages.join(', ')
        : null
    ].filter(Boolean).join(' | ');

    return [
      { label: 'Anon ID', value: event.anonymous_id },
      { label: 'Sesija', value: event.session_id },
      { label: 'User ID', value: event.user_id },
      { label: 'IP adresa', value: event.ip_address },
      { label: 'IP lokacija', value: formatAnalyticsLocation(properties) },
      { label: 'IP hash', value: event.ip_hash },
      { label: 'Browser', value: getAnalyticsBrowser(event, properties) },
      { label: 'OS', value: getAnalyticsOs(event, properties) },
      { label: 'Tip uredjaja', value: getAnalyticsDeviceType(event, properties) },
      {
        label: 'Ekran',
        value: formatAnalyticsResolution(properties.screen_width, properties.screen_height)
      },
      {
        label: 'Viewport',
        value: formatAnalyticsResolution(properties.viewport_width, properties.viewport_height)
      },
      { label: 'DPR', value: properties.device_pixel_ratio },
      { label: 'Jezik', value: language },
      {
        label: 'Timezone',
        value: properties.timezone || (
          properties.timezone_offset_minutes !== undefined
            ? `${properties.timezone_offset_minutes} min offset`
            : undefined
        )
      },
      { label: 'Platforma', value: getAnalyticsProperty(properties, ['ua_platform', 'platform']) },
      { label: 'Vendor', value: properties.vendor },
      { label: 'Cookies', value: properties.cookies_enabled },
      { label: 'Do Not Track', value: properties.do_not_track },
      { label: 'Touch points', value: properties.max_touch_points },
      {
        label: 'CPU hint',
        value: properties.hardware_concurrency ? `${properties.hardware_concurrency} cores` : undefined
      },
      {
        label: 'RAM hint',
        value: properties.device_memory_gb ? `${properties.device_memory_gb} GB` : undefined
      },
      { label: 'Online', value: properties.online },
      { label: 'Konekcija', value: formatAnalyticsConnection(properties) },
      { label: 'Referrer', value: properties.referrer, wide: true },
      { label: 'User agent', value: event.user_agent, wide: true }
    ];
  };

  const renderAnalyticsUser = (event) => {
    const primary = event.user_display ||
      event.username ||
      event.user_email ||
      (event.user_id ? `Korisnik ${event.user_id}` : 'Anonimno');

    const details = [];
    if (event.user_id) details.push(`ID ${event.user_id}`);
    if (event.user_email && event.user_email !== primary) details.push(event.user_email);
    if (!event.user_id && event.anonymous_id) details.push(`Anon ID ${getShortAnalyticsId(event.anonymous_id)}`);
    if (!event.user_id && event.session_id) details.push(`Sesija ${getShortAnalyticsId(event.session_id)}`);
    const detailRows = getAnalyticsDetailRows(event);

    return (
      <div className="analytics-user-cell">
        <strong>{primary}</strong>
        {details.length > 0 && <small>{details.join(' | ')}</small>}
        <details className="analytics-user-details">
          <summary>Detalji korisnika</summary>
          <dl>
            {detailRows.map((row) => (
              <div
                key={row.label}
                className={row.wide ? 'analytics-user-detail-row analytics-user-detail-row-wide' : 'analytics-user-detail-row'}
              >
                <dt>{row.label}</dt>
                <dd>{formatAnalyticsDetailValue(row.value)}</dd>
              </div>
            ))}
          </dl>
        </details>
      </div>
    );
  };

  const handleRecentFilterInputChange = (field, value) => {
    setAnalyticsRecentFilterInputs((currentFilters) => ({
      ...currentFilters,
      [field]: value
    }));
  };

  const handleApplyRecentColumnFilters = (event) => {
    event.preventDefault();
    const nextFilters = normalizeAnalyticsRecentFilters(analyticsRecentFilterInputs);
    setAnalyticsRecentFilters(nextFilters);
    setAnalyticsRecentFilterInputs(nextFilters);
    fetchAnalyticsRecentEvents(analyticsDays, analyticsExcludedUsers, nextFilters);
  };

  const handleClearRecentColumnFilters = () => {
    const emptyFilters = getEmptyAnalyticsRecentFilters();
    setAnalyticsRecentFilters(emptyFilters);
    setAnalyticsRecentFilterInputs(emptyFilters);
    fetchAnalyticsRecentEvents(analyticsDays, analyticsExcludedUsers, emptyFilters);
  };

  const recentAnalyticsEvents = analyticsSummary?.recent || [];
  const analyticsGroupings = analyticsSummary?.groupings || {};
  const analyticsUserGroups = analyticsGroupings.users || [];
  const analyticsIpGroups = analyticsGroupings.ip_addresses || [];
  const analyticsSessionGroups = analyticsGroupings.sessions || [];
  const hasRecentFilterInputs = hasAnalyticsRecentFilters(analyticsRecentFilterInputs);
  const hasRecentColumnFilters = hasAnalyticsRecentFilters(analyticsRecentFilters);
  const activeAnalyticsDetailFilterInputCount = ANALYTICS_DETAIL_FILTER_FIELDS.filter((filter) => (
    Boolean((analyticsRecentFilterInputs?.[filter.field] || '').trim())
  )).length;
  const activeAnalyticsDetailFilterCount = ANALYTICS_DETAIL_FILTER_FIELDS.filter((filter) => (
    Boolean((analyticsRecentFilters?.[filter.field] || '').trim())
  )).length;
  const hasAnalyticsDetailFilterInputs = activeAnalyticsDetailFilterInputCount > 0;
  const hasAnalyticsDetailFilters = activeAnalyticsDetailFilterCount > 0;

  if (!isAdmin) return null;

  return (
    <div className="admin-panel">
      <LogoTray />

      <div className="admin-content">
        <PageHeading eyebrow="Radni prostor" title="Administracija">Upravljajte sadržajem, korisnicima i postavkama portala.</PageHeading>
        {error && (
          <div className="error-message">
            {error}
            <button 
              className="error-close-btn" 
              onClick={() => setError('')}
            >
              ×
            </button>
          </div>
        )}

        <div className="admin-navigation">
          <button 
            className={`nav-button ${activeSection === 'categories' ? 'active' : ''}`}
            aria-pressed={activeSection === 'categories'}
            onClick={() => setActiveSection('categories')}
          >
            Kategorije
          </button>
          <button 
            className={`nav-button ${activeSection === 'users' ? 'active' : ''}`}
            aria-pressed={activeSection === 'users'}
            onClick={() => setActiveSection('users')}
          >
            Korisnici
          </button>
          <button
            className={`nav-button ${activeSection === 'analytics' ? 'active' : ''}`}
            aria-pressed={activeSection === 'analytics'}
            onClick={() => setActiveSection('analytics')}
          >
            Analitika
          </button>
          <button
            className={`nav-button ${activeSection === 'commercial' ? 'active' : ''}`}
            aria-pressed={activeSection === 'commercial'}
            onClick={() => setActiveSection('commercial')}
          >
            Reklame
          </button>
          <button
            className={`nav-button ${activeSection === 'notifications' ? 'active' : ''}`}
            aria-pressed={activeSection === 'notifications'}
            onClick={() => setActiveSection('notifications')}
            style={{ position: 'relative' }}
          >
            Obavijesti
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '2px',
                right: '4px',
                background: '#ff4444',
                color: '#fff',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 'bold',
                boxShadow: 'none'
              }}>
                {unreadCount}
              </span>
            )}
          </button>
          <button
            className={`nav-button ${activeSection === 'site-settings' ? 'active' : ''}`}
            aria-pressed={activeSection === 'site-settings'}
            onClick={() => setActiveSection('site-settings')}
          >
            Postavke stranice
          </button>
        </div>

        {activeSection === 'site-settings' && (
          <div className="site-settings-section">
            <h2>Postavke stranice</h2>
            <div style={{
              background: 'var(--card-bg-color, #202124)',
              borderRadius: '12px',
              border: '1px solid var(--border-color, #333)',
              padding: '24px',
              marginTop: '16px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h3 style={{ color: 'var(--heading-color, #e8eaed)', marginBottom: '6px', fontSize: '16px' }}>
                    Maintenance mod
                  </h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>
                    Kada je uključen, korisnici vide obavještenje "Radovi u toku" umjesto sadržaja stranice.
                  </p>
                </div>
                <div
                  className={`switch ${maintenanceMode ? 'green' : ''}`}
                  onClick={handleToggleMaintenance}
                  style={{ cursor: 'pointer', flexShrink: 0, marginLeft: '20px' }}
                >
                  <span className="switch-handle"></span>
                </div>
              </div>
              {maintenanceMode && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  background: 'rgba(255, 68, 68, 0.1)',
                  border: '1px solid rgba(255, 68, 68, 0.3)',
                  borderRadius: '8px',
                  color: '#ff6666',
                  fontSize: '13px'
                }}>
                  Maintenance mod je aktivan — korisnici trenutno ne mogu vidjeti sadržaj stranice.
                </div>
              )}
            </div>
          </div>
        )}

        {activeSection === 'analytics' && (
          <div className="analytics-management">
            <div className="analytics-header">
              <div>
                <h2>Analitika</h2>
                <p>Pregled klikova i aktivnosti korisnika u zadnjem periodu.</p>
              </div>
              <div className="analytics-controls">
                <div className="analytics-periods">
                  {[1, 7, 30, 90].map((days) => (
                    <button
                      key={days}
                      type="button"
                      className={analyticsDays === days ? 'active' : ''}
                      onClick={() => {
                        setAnalyticsDays(days);
                        fetchAnalyticsSummary(days, analyticsExcludedUsers, analyticsRecentFilters);
                      }}
                    >
                      {days === 1 ? '1 dan' : `${days} dana`}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="danger"
                    disabled={analyticsLoading || analyticsClearing}
                    data-analytics-ignore="true"
                    onClick={handleClearAllAnalytics}
                  >
                    {analyticsClearing ? 'Brisem...' : 'Clear all'}
                  </button>
                </div>
                <form
                  className="analytics-exclusions"
                  onSubmit={(event) => {
                    event.preventDefault();
                    fetchAnalyticsSummary(analyticsDays, analyticsExcludedUsers, analyticsRecentFilters);
                  }}
                >
                  <label htmlFor="analytics-excluded-users">Iskljuceni useri</label>
                  <div className="analytics-exclusion-row">
                    <input
                      id="analytics-excluded-users"
                      type="text"
                      value={analyticsExcludedUsers}
                      onChange={(event) => setAnalyticsExcludedUsers(event.target.value)}
                      placeholder="skaljic1, drugi_user"
                    />
                    <button type="submit" disabled={analyticsLoading}>Primijeni</button>
                  </div>
                  {(analyticsSummary?.excluded_users || []).length > 0 && (
                    <small>
                      Iskljuceno: {analyticsSummary.excluded_users.map((user) => user.username || user.email || `ID ${user.id}`).join(', ')}
                    </small>
                  )}
                  {(analyticsSummary?.unmatched_excluded_users || []).length > 0 && (
                    <small>
                      Nije nadjeno: {analyticsSummary.unmatched_excluded_users.join(', ')}
                    </small>
                  )}
                </form>
                {analyticsNotice && (
                  <small className="analytics-notice">{analyticsNotice}</small>
                )}
              </div>
            </div>

            {analyticsLoading ? (
              <p>Ucitavanje analitike...</p>
            ) : analyticsError ? (
              <div className="analytics-empty-state">
                {analyticsError}
              </div>
            ) : analyticsSummary ? (
              <>
                <div className="analytics-stats">
                  <div className="analytics-stat">
                    <span>Eventi</span>
                    <strong>{analyticsSummary.totals?.events || 0}</strong>
                  </div>
                  <div className="analytics-stat">
                    <span>Prijavljeni korisnici</span>
                    <strong>{analyticsSummary.totals?.known_users || 0}</strong>
                  </div>
                  <div className="analytics-stat">
                    <span>Anonimni browseri</span>
                    <strong>{analyticsSummary.totals?.anonymous_visitors || 0}</strong>
                  </div>
                  <div className="analytics-stat">
                    <span>Procijenjeni posjetioci</span>
                    <strong>{analyticsSummary.totals?.estimated_visitors || 0}</strong>
                  </div>
                  <div className="analytics-stat">
                    <span>Sesije</span>
                    <strong>{analyticsSummary.totals?.sessions || 0}</strong>
                  </div>
                  <div className="analytics-stat">
                    <span>IP adrese</span>
                    <strong>{analyticsSummary.totals?.ip_addresses || 0}</strong>
                  </div>
                </div>

                <div className="analytics-group-grid">
                  <div className="analytics-panel">
                    <h3>Korisnici po imenu</h3>
                    {analyticsUserGroups.length === 0 ? (
                      <p>Nema prijavljenih korisnika.</p>
                    ) : (
                      <ul className="analytics-list analytics-group-list">
                        {analyticsUserGroups.map((user) => (
                          <li key={user.id}>
                            <span>{user.display || user.username || user.email || `Korisnik ${user.id}`}</span>
                            <small>
                              {[user.name && user.username, user.email].filter(Boolean).join(' | ') || `ID ${user.id}`}
                            </small>
                            <small>
                              {user.sessions || 0} sesija | {user.ip_addresses || 0} IP | zadnji: {formatAnalyticsDate(user.last_seen) || '-'}
                            </small>
                            <strong>{user.count}</strong>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="analytics-panel">
                    <h3>Pristupi po IP adresi</h3>
                    {analyticsIpGroups.length === 0 ? (
                      <p>Nema IP podataka.</p>
                    ) : (
                      <ul className="analytics-list analytics-group-list">
                        {analyticsIpGroups.map((ipGroup) => (
                          <li key={ipGroup.ip_identity || ipGroup.ip_hash}>
                            <span>{getAnalyticsIpLabel(ipGroup.ip_address, ipGroup.ip_hash, ipGroup.country_label)}</span>
                            <small>
                              {(ipGroup.usernames || []).length > 0
                                ? `Useri: ${ipGroup.usernames.join(', ')}`
                                : 'Bez logovanog usera'}
                            </small>
                            <small>
                              {ipGroup.sessions || 0} sesija | {ipGroup.known_users || 0} prijavljenih | zadnji: {formatAnalyticsDate(ipGroup.last_seen) || '-'}
                            </small>
                            <strong>{ipGroup.count}</strong>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="analytics-panel">
                    <h3>Sesije</h3>
                    {analyticsSessionGroups.length === 0 ? (
                      <p>Nema sesija.</p>
                    ) : (
                      <ul className="analytics-list analytics-group-list">
                        {analyticsSessionGroups.map((session) => (
                          <li key={session.session_id}>
                            <span>{session.display || 'Anonimno'}</span>
                            <small>
                              Sesija {getShortAnalyticsId(session.session_id)} | IP {getAnalyticsIpLabel(session.ip_address, session.ip_hash, session.country_label)}
                            </small>
                            <small>
                              {session.pages || 0} stranica | zadnji: {formatAnalyticsDate(session.last_seen) || '-'}
                            </small>
                            <strong>{session.count}</strong>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="analytics-grid">
                  <div className="analytics-panel">
                    <h3>Najcesci eventi</h3>
                    {(analyticsSummary.events || []).length === 0 ? (
                      <p>Nema eventa.</p>
                    ) : (
                      <ul className="analytics-list">
                        {analyticsSummary.events.map((event) => (
                          <li key={event.event_name}>
                            <span>{event.event_name}</span>
                            <strong>{event.count}</strong>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="analytics-panel">
                    <h3>Najaktivniji korisnici</h3>
                    {(analyticsSummary.users || []).length === 0 ? (
                      <p>Nema prijavljenih korisnika.</p>
                    ) : (
                      <ul className="analytics-list">
                        {analyticsSummary.users.map((user) => (
                          <li key={user.id}>
                            <span>{user.username || user.email || `Korisnik ${user.id}`}</span>
                            <strong>{user.count}</strong>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="analytics-panel">
                    <h3>Najkliknutije mete</h3>
                    {(analyticsSummary.targets || []).length === 0 ? (
                      <p>Nema klikova.</p>
                    ) : (
                      <ul className="analytics-list">
                        {analyticsSummary.targets.slice(0, 10).map((target, index) => (
                          <li key={`${target.event_name}-${target.target}-${index}`}>
                            <span>{target.target}</span>
                            <small>{target.event_name}</small>
                            <strong>{target.count}</strong>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="analytics-panel">
                    <h3>Stranice</h3>
                    {(analyticsSummary.pages || []).length === 0 ? (
                      <p>Nema podataka.</p>
                    ) : (
                      <ul className="analytics-list">
                        {analyticsSummary.pages.map((page) => (
                          <li key={page.page}>
                            <span>{page.page}</span>
                            <strong>{page.count}</strong>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="analytics-panel analytics-wide">
                  <h3>Najkliknutiji clanci</h3>
                  {(analyticsSummary.articles || []).length === 0 ? (
                    <p>Nema klikova na clanke.</p>
                  ) : (
                    <div className="analytics-table-wrap">
                      <table className="analytics-table">
                        <thead>
                          <tr>
                            <th>Clanak</th>
                            <th>Portal</th>
                            <th>Klikovi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsSummary.articles.map((article) => (
                            <tr key={article.link || article.title}>
                              <td>
                                {article.link ? (
                                  <a href={article.link} target="_blank" rel="noopener noreferrer">
                                    {article.title}
                                  </a>
                                ) : article.title}
                              </td>
                              <td>{article.portal || '-'}</td>
                              <td>{article.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="analytics-panel analytics-wide" ref={analyticsRecentPanelRef}>
                  <form
                    className="analytics-recent-table-form"
                    onSubmit={handleApplyRecentColumnFilters}
                  >
                    <div className="analytics-panel-header">
                      <h3>Zadnji eventi</h3>
                      <div className="analytics-recent-actions">
                        {analyticsRecentLoading && <span>Filtriranje...</span>}
                        {analyticsRecentError && <span className="analytics-recent-error">{analyticsRecentError}</span>}
                        {(hasRecentFilterInputs || hasRecentColumnFilters) && (
                          <button
                            type="button"
                            className="secondary"
                            disabled={analyticsRecentLoading}
                            onClick={handleClearRecentColumnFilters}
                          >
                            Ocisti filtere
                          </button>
                        )}
                        <button type="submit" disabled={analyticsRecentLoading}>
                          Primijeni filtere
                        </button>
                      </div>
                    </div>
                    <details
                      className="analytics-detail-filter-panel"
                      open={hasAnalyticsDetailFilterInputs || hasAnalyticsDetailFilters}
                    >
                      <summary>
                        Filter detalja korisnika
                        {(hasAnalyticsDetailFilterInputs || hasAnalyticsDetailFilters) && (
                          <span>
                            {activeAnalyticsDetailFilterInputCount || activeAnalyticsDetailFilterCount} aktivno
                          </span>
                        )}
                      </summary>
                      <div className="analytics-detail-filter-grid">
                        {ANALYTICS_DETAIL_FILTER_FIELDS.map((filter) => (
                          <label
                            key={filter.field}
                            className={filter.wide ? 'analytics-detail-filter-field analytics-detail-filter-field-wide' : 'analytics-detail-filter-field'}
                          >
                            <span>{filter.label}</span>
                            <input
                              type="search"
                              value={analyticsRecentFilterInputs[filter.field] || ''}
                              onChange={(event) => handleRecentFilterInputChange(filter.field, event.target.value)}
                              placeholder={filter.placeholder}
                              aria-label={`Filter po detalju: ${filter.label}`}
                            />
                          </label>
                        ))}
                      </div>
                    </details>
                    <div className="analytics-table-wrap">
                      <table className="analytics-table">
                        <thead>
                          <tr>
                            <th>Vrijeme</th>
                            <th>Event</th>
                            <th>Korisnik</th>
                            <th>Meta</th>
                            <th>Stranica</th>
                          </tr>
                          <tr className="analytics-filter-row">
                            <th>
                              <input
                                type="search"
                                value={analyticsRecentFilterInputs.created_at}
                                onChange={(event) => handleRecentFilterInputChange('created_at', event.target.value)}
                                placeholder="Datum/vrijeme"
                                aria-label="Filter po vremenu"
                              />
                            </th>
                            <th>
                              <input
                                type="search"
                                value={analyticsRecentFilterInputs.event_name}
                                onChange={(event) => handleRecentFilterInputChange('event_name', event.target.value)}
                                placeholder="Event"
                                aria-label="Filter po eventu"
                              />
                            </th>
                            <th>
                              <input
                                type="search"
                                value={analyticsRecentFilterInputs.user}
                                onChange={(event) => handleRecentFilterInputChange('user', event.target.value)}
                                placeholder="Korisnik"
                                aria-label="Filter po korisniku"
                              />
                            </th>
                            <th>
                              <input
                                type="search"
                                value={analyticsRecentFilterInputs.target}
                                onChange={(event) => handleRecentFilterInputChange('target', event.target.value)}
                                placeholder="Meta"
                                aria-label="Filter po meti"
                              />
                            </th>
                            <th>
                              <input
                                type="search"
                                value={analyticsRecentFilterInputs.page}
                                onChange={(event) => handleRecentFilterInputChange('page', event.target.value)}
                                placeholder="Stranica"
                                aria-label="Filter po stranici"
                              />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentAnalyticsEvents.length === 0 ? (
                            <tr>
                              <td className="analytics-table-empty" colSpan="5">
                                {hasRecentColumnFilters
                                  ? 'Nema eventa za unesene filtere.'
                                  : 'Nema nedavnih eventa.'}
                              </td>
                            </tr>
                          ) : (
                            recentAnalyticsEvents.map((event) => (
                              <tr key={event.id}>
                                <td>{formatAnalyticsDate(event.created_at)}</td>
                                <td>{event.event_name}</td>
                                <td>{renderAnalyticsUser(event)}</td>
                                <td>{event.target || '-'}</td>
                                <td>{event.page || '-'}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </form>
                </div>
              </>
            ) : (
              <div className="analytics-empty-state">
                Nema ucitane analitike.
              </div>
            )}
          </div>
        )}

        {activeSection === 'categories' && (
          <div className="category-management">
            <h2>Kategorije</h2>
            
            <div className="add-category">
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Naziv nove kategorije"
              />
              <button onClick={handleAddCategory}>Dodaj kategoriju</button>
            </div>

            <div className="add-subcategory">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">Odaberi kategoriju</option>
                {Object.keys(categories).map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <input
                type="text"
                value={newSubcategory}
                onChange={(e) => setNewSubcategory(e.target.value)}
                placeholder="Naziv nove podkategorije"
              />
              <button onClick={handleAddSubcategory}>Dodaj podkategoriju</button>
            </div>

            <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
              <div className="categories-list">
                {Object.entries(categories).map(([category, subcategories]) => (
                  <div key={category} className="category-item">
                    <div className="category-header">
                      {editingCategory === category ? (
                        <div className="category-edit-form">
                          <input
                            type="text"
                            value={editingCategoryName}
                            onChange={(e) => setEditingCategoryName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveCategory();
                              } else if (e.key === 'Escape') {
                                handleCancelEditCategory();
                              }
                            }}
                            className="category-edit-input"
                            autoFocus
                          />
                          <button 
                            onClick={handleSaveCategory}
                            className="save-category-btn"
                            title="Spremi promjene"
                          >
                            ✓
                          </button>
                          <button 
                            onClick={handleCancelEditCategory}
                            className="cancel-category-btn"
                            title="Odustani"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <>
                          <h3>{category}</h3>
                          <div className="category-actions">
                            {category !== 'OSTALO' && (
                              <>
                                <button 
                                  onClick={() => handleEditCategory(category)}
                                  className="edit-category-btn"
                                  title="Uredi ime kategorije"
                                >
                                  <ControlIcon name="edit" />
                                </button>
                                <button 
                                  onClick={() => handleDeleteCategory(category)}
                                  className="delete-category-btn"
                                  title="Izbriši kategoriju"
                                >
                                  <ControlIcon name="trash" />
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <Droppable droppableId={category}>
                      {(provided, snapshot) => (
                        <ul
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`subcategories-list ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                          style={{
                            minHeight: '50px',
                            border: snapshot.isDraggingOver ? '2px dashed var(--accent)' : '1px solid var(--border)',
                            backgroundColor: snapshot.isDraggingOver ? 'var(--accent-glow)' : 'transparent'
                          }}
                        >
                          {subcategories.map((subcategory, index) => (
                            <Draggable 
                              key={subcategory} 
                              draggableId={subcategory} 
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <li
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`subcategory-item ${snapshot.isDragging ? 'dragging' : ''}`}
                                  style={{
                                    ...provided.draggableProps.style,
                                    cursor: snapshot.isDragging ? 'grabbing' : 'grab'
                                  }}
                                >
                                  <span className="subcategory-text">{subcategory}</span>
                                  <div className="subcategory-actions">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSubcategoryClick(subcategory, category, e);
                                      }}
                                      className="move-subcategory-btn"
                                      title="Premjesti u drugu kategoriju"
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      <ControlIcon name="portals" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteSubcategory(category, subcategory);
                                      }}
                                      className="delete-subcategory-btn"
                                      title={category === 'Ostalo' ? 'Trajno izbriši podkategoriju' : 'Premjesti u Ostalo'}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      ×
                                    </button>
                                  </div>
                                </li>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </ul>
                      )}
                    </Droppable>
                  </div>
                ))}
              </div>
            </DragDropContext>
          </div>
        )}

        {activeSection === 'users' && (
          <div className="user-management">
            <div className="user-management-header">
              <h2>Korisnici</h2>
              <button 
                onClick={() => setShowCreateUserModal(true)}
                className="create-user-btn"
              >
                Napravi korisnika
              </button>
            </div>
            
            <div className="user-search">
              <input
                type="text"
                placeholder="Pretraži korisnike..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="users-list">
              <table>
                <thead>
                  <tr>
                    <th>Korisničko ime</th>
                    <th>Email</th>
                    <th>Ime</th>
                    <th>Datum rođenja</th>
                    <th>Spol</th>
                    <th>Telefon</th>
                    <th>Admin status</th>
                    <th>Verifikovan</th>
                    <th>Banovan</th>
                    <th>Akcije</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id}>
                      {editingUser === user.id ? (
                        // Edit mode
                        <>
                          <td>
                            <input
                              type="text"
                              defaultValue={user.username}
                              title={USERNAME_HELP_TEXT}
                              autoComplete="username"
                              onChange={(e) => user.username = e.target.value}
                            />
                          </td>
                          <td>
                            <input
                              type="email"
                              defaultValue={user.email}
                              onChange={(e) => user.email = e.target.value}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              defaultValue={user.name}
                              onChange={(e) => user.name = e.target.value}
                            />
                          </td>
                          <td>
                            <input
                              type="date"
                              defaultValue={user.birth_date}
                              onChange={(e) => user.birth_date = e.target.value}
                            />
                          </td>
                          <td>
                            <select
                              value={user.gender || ''}
                              onChange={(e) => {
                                const selectedValue = e.target.value;
                                // Only update the user object if a valid gender is selected
                                if (selectedValue === '') {
                                  delete user.gender;  // Remove gender property completely if not selected
                                } else {
                                  user.gender = selectedValue;
                                }
                              }}
                            >
                              <option value="">Nije odabrano</option>
                              <option value="M">Muško</option>
                              <option value="F">Žensko</option>
                              <option value="O">Ostalo</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="tel"
                              defaultValue={user.phone}
                              onChange={(e) => user.phone = e.target.value}
                            />
                          </td>
                          <td>
                            <button 
                              onClick={() => handleToggleUserAdmin(user.id, user.admin)}
                              className={user.admin ? 'admin-status active' : 'admin-status'}
                            >
                              {user.admin ? 'Admin' : 'Korisnik'}
                            </button>
                          </td>
                          <td>
                            <button 
                              onClick={() => handleToggleUserVerification(user.id, user.verified)}
                              className={user.verified ? 'admin-status active' : 'admin-status'}
                            >
                              {user.verified ? 'Da' : 'Ne'}
                            </button>
                          </td>
                          <td>
                            <button 
                              onClick={() => handleToggleUserBan(user.id, user.banned)}
                              className={user.banned ? 'admin-status active' : 'admin-status'}
                            >
                              {user.banned ? 'Da' : 'Ne'}
                            </button>
                          </td>
                          <td>
                            <button 
                              onClick={() => {
                                handleUpdateUser(user.id, user);
                                setActiveSection('users');  // Return to users section after save
                              }}
                              className="save-btn"
                            >
                              Spremi
                            </button>
                            <button 
                              onClick={() => setEditingUser(null)}
                              className="cancel-btn"
                            >
                              Odustani
                            </button>
                          </td>
                        </>
                      ) : (
                        // View mode
                        <>
                          <td>{user.username}</td>
                          <td>{user.email}</td>
                          <td>{user.name}</td>
                          <td>{user.birth_date}</td>
                          <td>{user.gender}</td>
                          <td>{user.phone}</td>
                          <td>
                            <button 
                              onClick={() => handleToggleUserAdmin(user.id, user.admin)}
                              className={user.admin ? 'admin-status active' : 'admin-status'}
                            >
                              {user.admin ? 'Admin' : 'Korisnik'}
                            </button>
                          </td>
                          <td>
                            <button 
                              onClick={() => handleToggleUserVerification(user.id, user.verified)}
                              className={user.verified ? 'admin-status active' : 'admin-status'}
                            >
                              {user.verified ? 'Da' : 'Ne'}
                            </button>
                          </td>
                          <td>
                            <button 
                              onClick={() => handleToggleUserBan(user.id, user.banned)}
                              className={user.banned ? 'admin-status active' : 'admin-status'}
                            >
                              {user.banned ? 'Da' : 'Ne'}
                            </button>
                          </td>
                          <td>
                            <button 
                              onClick={() => setEditingUser(user.id)}
                              className="edit-btn"
                            >
                              Uredi
                            </button>
                            <button 
                              onClick={() => setPasswordChangeUserId(user.id)}
                              className="password-btn"
                            >
                              Promijeni lozinku
                            </button>
                            <button 
                              onClick={() => handleDeleteUser(user.id)}
                              className="delete-btn"
                            >
                              Izbriši
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeSection === 'commercial' && (
          <div className="commercial-management">
            <h2>Reklame</h2>
            <div className="banner-controls">
              <label htmlFor="banner-selector">Reklama za uređivanje</label>
              <select id="banner-selector" value={selectedBannerId} onChange={event => changeBannerSelection(event.target.value)}>
                <option value="" disabled>{previewUrl && !banners.length ? 'Postojeća reklama' : 'Odaberi reklamu'}</option>
                {banners.map((banner, index) => (
                  <option key={banner.id} value={banner.id}>Reklama {index + 1}{banner.isInitial ? ' (početna)' : ''}</option>
                ))}
              </select>
              <button type="button" className="new-banner-button" onClick={startNewBanner}>Nova reklama</button>
              {selectedBannerId && <div className="banner-actions">
                <label htmlFor="banner-position">Pomjeri na poziciju</label>
                <select id="banner-position"
                  value={banners.findIndex(b => String(b.id) === selectedBannerId) + 1}
                  onChange={event => moveBannerToPosition(Number(event.target.value) - 1)}>
                  {banners.map((banner, index) => <option key={banner.id} value={index + 1}>{index + 1}</option>)}
                </select>
                <label className="banner-initial-choice">
                  <input type="checkbox"
                    checked={Boolean(banners.find(b => String(b.id) === selectedBannerId)?.isInitial)}
                    disabled={Boolean(banners.find(b => String(b.id) === selectedBannerId)?.isInitial)}
                    onChange={() => updateBannerCollection(`/admin/commercial-banners/${selectedBannerId}/initial`, 'PUT')} />
                  Postavi kao početnu
                </label>
                <button type="button" onClick={() => {
                  if (window.confirm('Obrisati odabranu reklamu?')) {
                    updateBannerCollection(`/admin/commercial-banners/${selectedBannerId}`, 'DELETE', null, '');
                  }
                }}>Obriši</button>
              </div>}
            </div>
            <div className="commercial-form">
              <div className="current-banner">
                <h3>{selectedBannerId || previewUrl ? 'Odabrana reklama:' : 'Nova reklama:'}</h3>
                <p className="portal-preview-label">Izgled na portalu:</p>

                {/* Portal-style preview — always visible */}
                {bannerFetching ? (
                  <div style={{
                    width: '100%',
                    height: '120px',
                    borderRadius: '10px',
                    background: 'linear-gradient(145deg, #1a1a2e, #0f0f1f)',
                    border: '1px solid rgba(0, 255, 255, 0.3)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    margin: '8px 0 12px 0',
                    position: 'relative',
                  }}>
                    <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: 'var(--text-secondary)', fontSize: '14px' }}>Učitavanje...</span>
                  </div>
                ) : previewUrl ? (
                  <div style={{
                    width: '100%',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    margin: '8px 0 12px 0',
                    background: 'linear-gradient(145deg, #1a1a2e, #0f0f1f)',
                    border: '1px solid rgba(0, 255, 255, 0.3)',
                  }}>
                    <img
                      src={previewUrl}
                      alt="Preview reklame"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  </div>
                ) : (
                  <div style={{
                    width: '100%',
                    height: '120px',
                    borderRadius: '10px',
                    background: 'linear-gradient(145deg, #1a1a2e, #0f0f1f)',
                    border: '1px solid rgba(0, 255, 255, 0.3)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    margin: '8px 0 12px 0',
                    position: 'relative',
                  }}>
                    <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: 'var(--text-secondary)', fontSize: '14px' }}>Nema postavljene reklame</span>
                  </div>
                )}

                {previewUrl && (
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px' }}>
                    <span>Dimenzije: {commercialDimensions.width} × {commercialDimensions.height} px</span>
                    <span style={{ marginLeft: '16px' }}>Prikaz: {commercialDimensions.displayWidth} × {commercialDimensions.displayHeight} px</span>
                  </div>
                )}

                {(previewUrl || originalImageUrl) && (
                  <button
                    className="edit-banner-btn-standalone"
                    onClick={() => {
                      const imageToUse = originalImageUrl || previewUrl;
                      if (imageToUse) {
                        setCropperImage(imageToUse);
                        setShowCropper(true);
                      } else {
                        setError('Nema slike za uređivanje');
                      }
                    }}
                  >
                    Uredi veličinu trenutne reklame
                  </button>
                )}
              </div>
              
              <div className="form-group">
                <label>Nova slika reklame:</label>
                <div className="custom-file-input">
                  <input
                    type="file"
                    accept="image/*,.jfif,.webp"
                    onChange={handleImageChange}
                    className="file-input"
                    id="banner-file-input"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="banner-file-input" className="file-input-label">
                    Odaberi sliku
                  </label>
                </div>
                <p className="file-help">Odaberite sliku za obrezivanje i promjenu veličine</p>
              </div>

              <div className="form-group" style={{marginTop: '10px'}}>
                <label>Link reklame (otvara se u novom tabu):</label>
                <input
                  type="url"
                  value={commercial.link || ''}
                  onChange={(e) => setCommercial({...commercial, link: e.target.value})}
                  placeholder="https://example.com"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color, #333)',
                    background: 'var(--input-bg-color, #202124)',
                    color: 'var(--text-color, #e8eaed)',
                    fontSize: '13px',
                    marginTop: '6px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="banner-duration">Vrijeme prikaza prije sljedeće reklame (sekunde):</label>
                <input id="banner-duration" type="number" min="1" max="86400" step="1"
                  value={bannerDuration} onChange={event => setBannerDuration(event.target.value)} />
              </div>

              <button
                className="update-commercial-btn"
                onClick={() => {
                  console.log('🔘 Update button clicked');
                  console.log('🔘 Current state:', {
                    commercialImage: !!commercialImage,
                    previewUrl: !!previewUrl,
                    originalImageUrl: !!originalImageUrl,
                    commercialDimensions
                  });
                  handleUpdateCommercial();
                }}
                disabled={(!commercialImage && !previewUrl) || !Number.isInteger(Number(bannerDuration)) || Number(bannerDuration) < 1}
              >
                {selectedBannerId ? 'Sačuvaj reklamu' : 'Dodaj reklamu'}
              </button>
            </div>
          </div>
        )}

        {activeSection === 'notifications' && (
          <div className="notification-management">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Obavijesti ({notificationsTotal})</h2>
              {notifications.length > 0 && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="mark-all-read-btn"
                    onClick={handleMarkAllRead}
                    style={{
                      padding: '8px 16px',
                      background: 'var(--accent-glow)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Označi sve kao pročitano
                  </button>
                  <button
                    onClick={handleDeleteAllNotifications}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(210, 72, 72, .12)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Obriši sve
                  </button>
                </div>
              )}
            </div>

            {notifications.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: 'var(--text-secondary)',
                fontSize: '15px',
                background: 'var(--card-bg-color, #202124)',
                borderRadius: '12px',
                border: '1px solid var(--border-color, #333)'
              }}>
                Nema obavijesti
              </div>
            ) : (
              <div className="notifications-list">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="notification-item"
                    style={{
                      padding: '14px 18px',
                      marginBottom: '8px',
                      borderRadius: '10px',
                      background: notification.is_read
                        ? 'var(--card-bg-color, #202124)'
                        : 'var(--accent-glow)',
                      border: notification.is_read
                        ? '1px solid var(--border-color, #333)'
                        : '1px solid rgba(255, 68, 68, 0.4)',
                      boxShadow: notification.is_read
                        ? 'none'
                        : 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '12px'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        {!notification.is_read && (
                          <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#ff4444',
                            display: 'inline-block',
                            flexShrink: 0
                          }} />
                        )}
                        <strong style={{ color: 'var(--heading-color, #e8eaed)', fontSize: '14px' }}>
                          {notification.portal_name}
                        </strong>
                        <span style={{ color: 'var(--text-muted)', fontSize: '12px', marginLeft: 'auto' }}>
                          {new Date(notification.created_at).toLocaleString('bs-BA', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p style={{ margin: '4px 0 0 0', color: 'var(--text-secondary)', fontSize: '13px' }}>
                        {notification.error_message}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      {!notification.is_read && (
                        <button
                          onClick={() => handleMarkNotificationRead(notification.id)}
                          title="Označi kao pročitano"
                          style={{
                            background: 'transparent',
                            border: '1px solid #555',
                            color: 'var(--text-secondary)',
                            borderRadius: '6px',
                            padding: '4px 10px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ✓
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteNotification(notification.id)}
                        title="Izbriši"
                        style={{
                          background: 'transparent',
                          border: '1px solid #555',
                          color: '#ff6666',
                          borderRadius: '6px',
                          padding: '4px 10px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {passwordChangeUserId && (
        <PasswordChangeModal
          userId={passwordChangeUserId}
          onClose={() => setPasswordChangeUserId(null)}
          onPasswordChange={handlePasswordChange}
        />
      )}

      {showCreateUserModal && (
        <CreateUserModal
          onClose={() => setShowCreateUserModal(false)}
          onCreateUser={handleCreateUser}
        />
      )}

      {showCropper && (
        <ImageCropper
          image={cropperImage}
          onCrop={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={null}
          minWidth={200}
          minHeight={100}
          maxWidth={1200}
          maxHeight={600}
          maxCropWidth={3000}
          maxCropHeight={3000}
          showSearchDisplayArea={false}
          initialDimensions={commercialDimensions.width > 0 ? {
            width: commercialDimensions.width,
            height: commercialDimensions.height,
            displayWidth: commercialDimensions.displayWidth,
            displayHeight: commercialDimensions.displayHeight,
            cropX: commercialDimensions.cropX,
            cropY: commercialDimensions.cropY
          } : null}
        />
      )}

      {showCategoryModal && (
        <div className="category-modal-overlay" onClick={() => {
          setShowCategoryModal(false);
          setSelectedSubcategoryForMove(null);
          setSelectedSubcategoryCurrentCategory(null);
        }}>
          <div className="category-modal" onClick={(e) => e.stopPropagation()}>
            <div className="category-modal-header">
              <h3>Odaberi kategoriju za "{selectedSubcategoryForMove}"</h3>
              <button 
                className="category-modal-close"
                onClick={() => {
                  setShowCategoryModal(false);
                  setSelectedSubcategoryForMove(null);
                  setSelectedSubcategoryCurrentCategory(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="category-move-search">
              <label htmlFor="category-move-search">Pretraži kategorije</label>
              <input id="category-move-search" type="search" autoFocus value={categoryMoveSearch} onChange={event => setCategoryMoveSearch(event.target.value)} placeholder="Naziv kategorije…" />
            </div>
            <div className="category-modal-content">
              {Object.keys(categories).filter(name => name.toLocaleLowerCase().includes(categoryMoveSearch.trim().toLocaleLowerCase())).length === 0 && <p role="status">Nema kategorija za ovu pretragu.</p>}
              {Object.keys(categories).filter(name => name.toLocaleLowerCase().includes(categoryMoveSearch.trim().toLocaleLowerCase())).map((categoryName) => (
                <button
                  key={categoryName}
                  className={`category-modal-item ${categoryName === selectedSubcategoryCurrentCategory ? 'current-category' : ''}`}
                  onClick={() => handleMoveSubcategoryToCategory(categoryName)}
                  disabled={categoryName === selectedSubcategoryCurrentCategory}
                >
                  {categoryName}
                  {categoryName === selectedSubcategoryCurrentCategory && (
                    <span className="current-label">(trenutna)</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
